import React, { createContext, useCallback, useContext, useState } from "react";
import { LivingToolsTab } from "./types";

export interface LivingToolsUiState {
  activeTab: LivingToolsTab;
  expanded: boolean;
  pinned: boolean;
  setActiveTab: (tab: LivingToolsTab) => void;
  setExpanded: (expanded: boolean) => void;
  openTab: (tab: LivingToolsTab, pin?: boolean) => void;
  minimize: () => void;
  setPinned: (pinned: boolean) => void;
}

const LivingToolsContext = createContext<LivingToolsUiState | null>(null);

export function LivingToolsProvider({
  children,
  propertyId,
}: {
  children: React.ReactNode;
  propertyId: number;
}) {
  const [activeTab, setActiveTab] = useState<LivingToolsTab>("plan");
  const [expanded, setExpanded] = useState(false);
  const [pinned, setPinned] = useState(false);

  const openTab = useCallback((tab: LivingToolsTab, pin = true) => {
    setActiveTab(tab);
    setExpanded(true);
    if (pin) setPinned(true);
  }, []);

  const minimize = useCallback(() => {
    setExpanded(false);
    setPinned(false);
  }, []);

  React.useEffect(() => {
    setPinned(false);
    setExpanded(false);
  }, [propertyId]);

  return (
    <LivingToolsContext.Provider
      value={{
        activeTab,
        expanded,
        pinned,
        setActiveTab,
        setExpanded,
        openTab,
        minimize,
        setPinned,
      }}
    >
      {children}
    </LivingToolsContext.Provider>
  );
}

export function useLivingToolsUi(): LivingToolsUiState {
  const ctx = useContext(LivingToolsContext);
  if (!ctx) {
    throw new Error("useLivingToolsUi must be used within LivingToolsProvider");
  }
  return ctx;
}

export function useLivingToolsUiOptional(): LivingToolsUiState | null {
  return useContext(LivingToolsContext);
}
